import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Rocket,
  Brain,
  CreditCard,
  Zap,
  Wrench,
  HelpCircle,
  Users,
  Image,
  Lightbulb,
  LucideIcon,
} from 'lucide-react';
import { PublicHeader } from '@/components/Layout/PublicHeader';
import { PublicFooter } from '@/components/Layout/PublicFooter';
import { HelpSearchBar, HelpBreadcrumb, ArticleListItem } from '@/components/help';
import { helpService, HelpCategory as HelpCategoryType, HelpArticle } from '@/services/helpService';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/lib/utils';

const iconMap: Record<string, LucideIcon> = {
  Rocket,
  Brain,
  CreditCard,
  Zap,
  Wrench,
  HelpCircle,
  Users,
  Image,
  Lightbulb,
};

const colorMap: Record<string, { bg: string; text: string }> = {
  purple: { bg: 'bg-purple-100', text: 'text-purple-600' },
  violet: { bg: 'bg-violet-100', text: 'text-violet-600' },
  blue: { bg: 'bg-blue-100', text: 'text-blue-600' },
  green: { bg: 'bg-green-100', text: 'text-green-600' },
  orange: { bg: 'bg-orange-100', text: 'text-orange-600' },
  yellow: { bg: 'bg-yellow-100', text: 'text-yellow-600' },
  red: { bg: 'bg-red-100', text: 'text-red-600' },
  gray: { bg: 'bg-gray-100', text: 'text-gray-600' },
};

// Category metadata - matches server/http/routes/help.routes.ts
const categoryMeta: Record<string, { icon: string; color: string; description: string }> = {
  'getting-started': { icon: 'Rocket', color: 'purple', description: 'Learn the basics of Cascady and get up to speed quickly' },
  'canvas-editor': { icon: 'Zap', color: 'blue', description: 'Build and manage visual AI workflows on the canvas' },
  'ai-models-generation': { icon: 'Brain', color: 'violet', description: 'Understanding and using AI image and video generation' },
  'account-settings': { icon: 'CreditCard', color: 'green', description: 'Manage your account, preferences, and settings' },
  'organization-users': { icon: 'Users', color: 'orange', description: 'Manage your team and organization settings' },
  'gallery-assets': { icon: 'Image', color: 'blue', description: 'Organize and manage your generated images and videos' },
  'tips-tricks': { icon: 'Lightbulb', color: 'yellow', description: 'Pro tips to get the most out of Cascady' },
  'troubleshooting': { icon: 'Wrench', color: 'red', description: 'Solutions to common issues and problems' },
};

export function HelpCategory() {
  const { slug } = useParams<{ slug: string }>();
  const [category, setCategory] = useState<{ slug: string; name: string } | null>(null);
  const [articles, setArticles] = useState<HelpArticle[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    async function loadData() {
      if (!slug) return;
      try {
        const data = await helpService.getCategory(slug);
        setCategory(data.category);
        setArticles(data.articles);
      } catch (err) {
        console.error('Failed to load category:', err);
        setError(true);
      } finally {
        setIsLoading(false);
      }
    }
    loadData();
  }, [slug]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white">
        <PublicHeader />
        <div className="flex justify-center items-center min-h-[60vh]">
          <Spinner size="lg" />
        </div>
        <PublicFooter />
      </div>
    );
  }

  if (error || !category) {
    return (
      <div className="min-h-screen bg-white">
        <PublicHeader />
        <div className="container mx-auto px-4 max-w-4xl py-16 text-center">
          <h1 className="text-2xl font-semibold text-gray-900 mb-4">Category Not Found</h1>
          <p className="text-gray-600">The category you're looking for doesn't exist.</p>
        </div>
        <PublicFooter />
      </div>
    );
  }

  const meta = categoryMeta[slug || ''] || { icon: 'HelpCircle', color: 'gray', description: '' };
  const Icon = iconMap[meta.icon] || HelpCircle;
  const colors = colorMap[meta.color] || colorMap.gray;

  const pageTitle = `${category.name} | Help Center | Cascady`;
  const pageDescription = meta.description || `Browse ${category.name} articles in the Cascady Help Center.`;

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://cascady.anacreon.ai/help/${slug}`} />
      </Helmet>
      <PublicHeader />

      <div className="container mx-auto px-4 max-w-4xl py-8">
        {/* Breadcrumb */}
        <HelpBreadcrumb
          items={[{ label: category.name }]}
        />

        {/* Search Bar */}
        <div className="mb-8">
          <HelpSearchBar />
        </div>

        {/* Category Header */}
        <div className="flex items-start gap-3 sm:gap-4 mb-6 sm:mb-8">
          <div
            className={cn(
              'w-10 h-10 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center flex-shrink-0',
              colors.bg
            )}
          >
            <Icon className={cn('w-5 h-5 sm:w-7 sm:h-7', colors.text)} />
          </div>
          <div>
            <h1 className="text-xl sm:text-3xl font-semibold text-gray-900 mb-1 sm:mb-2">
              {category.name}
            </h1>
            <p className="text-sm sm:text-base text-gray-600">{meta.description}</p>
          </div>
        </div>

        {/* Articles List */}
        {articles.length > 0 ? (
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <div className="divide-y divide-gray-100">
              {articles.map((article) => (
                <ArticleListItem key={article.slug} article={article} />
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center py-12 bg-gray-50 rounded-2xl">
            <p className="text-gray-500">No articles in this category yet.</p>
          </div>
        )}
      </div>

      <PublicFooter />
    </div>
  );
}
